import { Injectable, inject } from '@angular/core';
import { FilterService, MultiValueFilter, SearchFilter, DateFilter } from '@ceres/filter';
import { partition } from 'lodash-es';
import { IUser, ProjectProfileFilterValues, RoleType, DateRangeType } from '@ceres/domain';
import { ImpersonatedHttpClient, AppUserService, User } from '@ceres/shared/services';
import { ListItem } from '@ceres/frontend-helper';
import { AppSettingsService } from '@ceres/features/admin-general';
import * as _ from 'lodash';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';
import moment from 'moment';
import { ProjectProfile, BusinessArea } from '../../shared/models';
import { environment } from '../../../environments/environment';
import { PersistentFilter } from '../../myceres/components/settingsv2/custom-filter/custom-filter';
import projectListColumns from '../../../configs/project-list-columns/project-list-columns.config';
import { EmployeeSettingsService } from '../../shared/services/employee-settings.service';
import { DateService } from '../../shared/services/date.service';

const DEFAULT_PROJECT_STATUS = [
    'projects.project-property-options.status.in-progress',
    'projects.project-property-options.status.other',
    'projects.project-property-options.status.marked-for-deletion'
];

const searchFields = [
    'mpNumber::text',
    'mpTitle',
    'businessPartnerPre',
    'businessPartnerSur',
    'projectLead.name',
    'projektGruppierung',
    'projectStatus',
    'leistungsempfaenger.title',
    'orgID',
    'costCenterInternal',
    'bestellnummer',
    'angebotsnummer'
];

const isMerchant = ({ role }: User) => role && role.name === RoleType.Merchant;

@Injectable()
export class ProjectFilterService extends FilterService<ProjectProfile> {
    private readonly appUserService = inject(AppUserService);
    private readonly http = inject(ImpersonatedHttpClient);
    private employeeSettingsService = inject(EmployeeSettingsService);
    private readonly dateService = inject(DateService);
    private readonly appSettings = inject(AppSettingsService);

    private isFiscalYearMode = false;

    public columnFilters = [
        new MultiValueFilter('businessPartner.fullName'),
        new MultiValueFilter('abteilungKunde'),
        new MultiValueFilter('verrechnungszyklus'),
        new MultiValueFilter('leistungsempfaenger.title'),
        new MultiValueFilter('mpNumber'),
        new MultiValueFilter('projectLead.name'),
        new MultiValueFilter('projectStatus', [...DEFAULT_PROJECT_STATUS]),
        new MultiValueFilter('projektGruppierung'),
        new MultiValueFilter('verrechnungsart'),
        new MultiValueFilter('kundenart'),
        new MultiValueFilter('updatedByEmail'),
        new MultiValueFilter('program.title'),
        new MultiValueFilter('projectTags.translationKey'),
        new MultiValueFilter('projectTagsCategory1.translationKey'),
        new MultiValueFilter('projectTagsCategory2.translationKey'),
        new MultiValueFilter('maturityLevel.translationKey'),
        new MultiValueFilter('costCenterInternal'),
        new MultiValueFilter('customerPspElement.title'),
        new MultiValueFilter('projectType.translationKey'),
        new MultiValueFilter('verrechnungsland'),
        new MultiValueFilter('angebotsnummer'),
        new MultiValueFilter('transactionalBudget'),
        new MultiValueFilter('zfBudget'),
        new MultiValueFilter('discountApplicable'),
        new MultiValueFilter('extended')
    ];
    protected searchFilters = searchFields.map((key) => new SearchFilter(key));
    public globalFilters = [new DateFilter('startDate'), new DateFilter('endDate')];

    public initialColumnSetup: ListItem[] = [...projectListColumns];
    public availableColumns: ListItem[] = _.cloneDeep(this.initialColumnSetup);

    constructor() {
        super();
        const appUserService = this.appUserService;

        this.appUserService.loggedInUser$.subscribe((user) => {
            if (isMerchant(user)) {
                this.applyBusinessAreaFilter(user.details.businessArea);
            }
        });

        this.employeeSettingsService.persistentFilter$.subscribe((data) => {
            if (!data) {
                return;
            }
            this.applyPersistentFilters(data, appUserService.loggedInUser$.getValue());
        });
        this.applyFilter('projectStatus');
        this.appSettings.dateRangeType$.pipe(tap((dateRangeType) => this.setDateRangeMode(dateRangeType))).subscribe();
    }

    private setDateRangeMode(dateRangeType: DateRangeType): void {
        this.isFiscalYearMode = dateRangeType === DateRangeType.FiscalYear;
    }

    applyFiscalYear({ date, dateEnd }: { date: Date; dateEnd: Date }) {
        this.globalFilters
            .filter(({ key }) => key === 'startDate' || key === 'endDate')
            .forEach((filter) => {
                filter.start = date;
                filter.end = dateEnd;
                filter.isApplied = filter.isActive();
            });
        this.applyProjectStatusForRange(dateEnd);
        this.triggerFilter();
    }

    // while viewing a PAST fiscal year (FiscalYear mode only — Dynamic is left alone),
    // suppress the status filter so all projects show; restore whatever the user had (default,
    // reset, or a custom filter) when leaving the past. Only touches the status filter when
    // crossing the past boundary — normal status changes (reset/custom filter) are left alone.
    // Ceiling: a status change made *while* in a past FY is discarded on return (status is
    // irrelevant there); track it live if that ever matters.
    private suppressedProjectStatus: Array<string | number | boolean> | null = null;

    private applyProjectStatusForRange(dateEnd: Date): void {
        const statusFilter = this.getColumnFilters('projectStatus') as MultiValueFilter;
        if (!statusFilter) {
            return;
        }

        const currentFiscalYearStart = this.dateService.getBusinessYearsWithTimezoneAdjustment()[1].date;
        const isPastFiscalYear =
            this.isFiscalYearMode && moment(dateEnd).isBefore(moment(currentFiscalYearStart), 'day');

        if (isPastFiscalYear) {
            if (this.suppressedProjectStatus === null) {
                this.suppressedProjectStatus = statusFilter.selected;
                statusFilter.selected = [];
                this.setFilter(statusFilter);
            }
            return;
        }

        if (this.suppressedProjectStatus !== null) {
            statusFilter.selected = this.suppressedProjectStatus;
            this.suppressedProjectStatus = null;
            this.setFilter(statusFilter);
        }
    }

    private applyPersistentFilters(filtersFromSettings: PersistentFilter, user: IUser) {
        const transformedFilters = this.transformFiltersFromSettings(filtersFromSettings, user);
        if (!transformedFilters) {
            return;
        }

        const [persistentFilters, nonPersistentFilters] = partition(
            this.columnFilters,
            ({ key }) => transformedFilters[key] && transformedFilters[key].length
        );

        // push selected values
        persistentFilters.forEach((filter) => {
            filter.selected.push(...transformedFilters[filter.key].filter((val) => !filter.selected.includes(val)));
            filter.isApplied = filter.isActive();
        });
        this.columnFilters = [...persistentFilters, ...nonPersistentFilters];
        this.triggerFilter();
    }

    private applyBusinessAreaFilter(businessArea: BusinessArea) {
        if (businessArea && businessArea.title) {
            const groupingFilter = this.columnFilters.find(({ key }) => key === 'projektGruppierung');
            if (groupingFilter) {
                groupingFilter.selected.push(businessArea.title);
                this.applyFilter(groupingFilter);
            }
        }
    }

    protected fetchFilterValues(field: string, filters: string) {
        if (field === 'isFavorite') {
            return of({ isFavorite: [true, false] });
        }

        if (field === 'extended') {
            return of({ extended: [true, false] });
        }

        return this.http.get<ProjectProfileFilterValues>(`${environment.edgeService}/projects/profiles/filterValues`, {
            params: {
                field,
                filters
            }
        });
    }

    private transformFiltersFromSettings(filtersFromSettings: PersistentFilter, user: IUser) {
        if ('projectGrouping' in filtersFromSettings && 'onlyUserAsProjectLead' in filtersFromSettings) {
            return {
                projektGruppierung: filtersFromSettings.projectGrouping,
                'projectLead.name': filtersFromSettings.onlyUserAsProjectLead ? [user.details.name] : ''
            };
        } else if ('projectGrouping' in filtersFromSettings) {
            return {
                projektGruppierung: (filtersFromSettings as { projectGrouping }).projectGrouping
            };
        } else if ('onlyUserAsProjectLead' in filtersFromSettings) {
            return {
                'projectLead.name': (filtersFromSettings as { onlyUserAsProjectLead }).onlyUserAsProjectLead
                    ? [user.details.name]
                    : ''
            };
        } else {
            return false;
        }
    }

    public setColumnFilters(columnFilters: MultiValueFilter[]) {
        this.columnFilters = columnFilters;
    }
}
